import React, { useState } from 'react'
import "./AttachmentUploadContainer.scss";
import { CloudUpload, ExclamationCircleFill } from '@livingdesign/icons';
import { CAMPAIGN, CONFIGURATIONS } from '../../../../../../consts/DBFields';
import { useFileUpload } from '../../../../../../hooks/useFileUpload';
import { uploadCampaignAttachments } from '../../../../../../utils/campaign';
import { useCampaignContext } from '../../../../../../contexts/Campaign';
import UploadedAttachmentListItem from './UploadedAttachmentListItem';
import { Alert, Spinner } from '@livingdesign/react';
import UploadProgressItem from './UploadProgressItem';
import AttachmentIcon from "../../../../../../assets/svg/AttachmentIcon.svg"

export const QA_ID_PREFIX = "AttachmentUploadContainer";

function AttachmentUploadContainer({
    id,
    label,
    maxFiles,
    accept,
    fileUploadStateRef,
    sizeLimit,
    clearError,
    multiple,
    removable = true,
    getTotalFileSize = () => {},
    onChange,
    value = [],
    disabled
}) {
    const [error, setError] = useState(false); // a SAS upload error
    const [fileUploadingProgress, setFileUploadingProgress] = useState({});
    const [isFileUploading, setIsFileUploading] = useState(false);

    const {
        selectedCampaign,
        saveCampaign,
        notificationConfigurationFields,
    } = useCampaignContext();

    const handleChange = async (files, error) => {
        console.log({files});
        setIsFileUploading(true);
        setError(error);
        const updatedCampaign = notificationConfigurationFields[CONFIGURATIONS.ATTACHMENTS].handleChange(files);
        onChange(files, error);
        await saveCampaign(updatedCampaign);
        setIsFileUploading(false);
    }

    const {
        isUploading,
        fileError,
        fileNames,
        getRootProps,
        getInputProps,
    } = useFileUpload({
        files: value,
        maxFiles,
        accept,
        sizeLimit,
        handleUpload: async (files, existingFiles) => {
            let uploadError = false;
            const uploaded = [];
            await uploadCampaignAttachments(
                files,
                selectedCampaign?.[CAMPAIGN.TEAM_ID],
                selectedCampaign?.[CAMPAIGN.UID],
                async (newFile) => {
                    uploaded.push(newFile);
                },
                () => {
                    uploadError = true;
                    setError(true)
                },
                setFileUploadingProgress
            );
            const updatedValue = existingFiles ? existingFiles.concat(uploaded) : uploaded;
            handleChange(
                updatedValue,
                uploadError
            );
            setFileUploadingProgress({});
            setIsFileUploading(false)
            return uploaded;
        },
        clearError,
        multiple,
        getTotalFileSize,
        fileUploadStateRef,
        initialError: error
    });

    const removeFileAtIndex = async (index) => {
        let updatedValue = [...value];
        updatedValue.splice(index, 1);
        handleChange(updatedValue, false);
    }

    const inputProps = getInputProps();

    return (
        <div className='message-attachments-edit' data-testid={`${QA_ID_PREFIX}-container`}>
            <h3 data-testid={`${QA_ID_PREFIX}-label`}>{label}</h3>
            <div className='message-attachments-instructions' data-testid={`${QA_ID_PREFIX}-message-attachments-instructions`}>Please add files that you want to attach with your message. Adding attachments is optional.</div>
            <Alert
                variant='warning'
                data-testid={`${QA_ID_PREFIX}-alert`}
                UNSAFE_style={{marginBottom: "12px", marginTop: "12px"}}
            >
                CSV files with recipient information must not be uploaded as attachments. Upload them in the recipients section only.
            </Alert>
            <div data-testid={`${QA_ID_PREFIX}-upload-section`}>
                {(isUploading || isFileUploading || disabled) &&
                    <div className='upload-container-loading' data-testid={`${QA_ID_PREFIX}-upload-loading`}>
                        {!disabled && <Spinner size='large' data-testid={`${QA_ID_PREFIX}-spinner`} />}
                    </div>
                }
                <div className='upload-container' {...getRootProps({ refKey: 'rootPropsRef' })} data-testid={`${QA_ID_PREFIX}-upload-container`}>
                    <input {...inputProps} name={`${id}-FileUpload-Input`} role="File Upload" data-testid={`${QA_ID_PREFIX}-file-input`} />
                    <div className='upload-icon' data-testid={`${QA_ID_PREFIX}-upload-icon`}><img src={AttachmentIcon} alt="Upload Icon" /></div>
                    <div className='upload-text' data-testid={`${QA_ID_PREFIX}-upload-text`}>Drag and drop your files here</div>
                    <div className='upload-instructions' data-testid={`${QA_ID_PREFIX}-upload-instructions`}>You can upload up to 10 files in .png, .jpg, .ics, .xlsx, .pptx, .docx, .zip, or .pdf format, with a total size limit of 10 MB.</div>
                    <div style={{color: "#515357", padding: "8px 0"}} data-testid={`${QA_ID_PREFIX}-or-text`}>OR</div>
                    <div className='upload-button' data-testid={`${QA_ID_PREFIX}-browse-files`}>Browse files</div>
                </div>
            </div>
            <div className="upload-status" data-testid={`${QA_ID_PREFIX}-upload-status`}>
                {<div className="upload-progress" data-testid={`${QA_ID_PREFIX}-upload-progress`}>
                    {Object.keys(fileUploadingProgress).map((fileName, idx) => {
                        return (
                            <UploadProgressItem
                                key={fileName}
                                file={{name: fileName}}
                                progress={fileUploadingProgress[fileName]}
                                data-testid={`${QA_ID_PREFIX}-progress-item-${idx}`}
                            />
                        );
                    })}
                </div>}
                {fileError && <div className="upload-error" data-testid={`${QA_ID_PREFIX}-file-error`}><ExclamationCircleFill size='small' color='#ea1100' />&nbsp;&nbsp;{fileError}</div>}
                {error && <div className="upload-error" data-testid={`${QA_ID_PREFIX}-upload-error`}><ExclamationCircleFill size='small' color='#ea1100' />&nbsp;&nbsp;There was an error uploading your file(s).</div>}
                {value?.length > 0 && (
                    <div className="file-list" data-testid={`${QA_ID_PREFIX}-file-list`}>
                            {value?.map((file, index) => (
                                <UploadedAttachmentListItem
                                    key={`${file.filename}-${index}`}
                                    index={index}
                                    fileName={file.filename}
                                    fileSource={file.path}
                                    removable={removable}
                                    removeFileAtIndex={removeFileAtIndex}
                                    disbaled={isUploading || isFileUploading || disabled}
                                    data-testid={`${QA_ID_PREFIX}-file-list-item-${index}`}
                                />
                            ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default AttachmentUploadContainer;